/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Collapse,
  Button,
  Dialog,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import moment from "moment";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CurrencyRupee,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close,
  CheckCircleOutline,
  CancelOutlined,
  CalendarMonth,
  Restore,
} from "@mui/icons-material";
import { Stack } from "@mui/system";
import {
  IMultipleEvent,
  ILead,
  IEventExtras,
} from "../../../../redux/leads/leads.types";
import { LeadStatusRenderer } from "../../../../components/LeadStatusRenderer/LeadStatusRenderer";
import { CustomChip } from "../../../../components/CustomChip";
import { renderDropdownValue } from "../../../../helpers";
import { MasterStatusAutoSearch } from "../../../../components/AutoCompleteSearches/MasterStatusAutoSearch";
import type { IDatatableV2AdvancedSearchFilter } from "../../../../redux/common/common.types";
import { EventAutoSearch } from "../../../../components/AutoCompleteSearches/EventAutoSearch";
import {
  CustomNumberField,
  CustomTextField,
  CustomCurrencyFieldV3,
} from "../../../../components/formsComponents";
import { EventTimeAutoSearch } from "../../../../components/AutoCompleteSearches/EventTimeAutoSearch";
import { VenueAutoSearch } from "../../../../components/AutoCompleteSearches/VenueAutoSearch";
import { ControlledCustomSelect } from "../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../components/formsComponents/CustomDatePicker";
import { CancellationReasonAutoSearch } from "../../../../components/AutoCompleteSearches/CancellationReasonAutoSearch";
import { IMasterStatus } from "../../../../redux/status/status.types";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../redux/initialStoreState";
import { LoadState } from "../../../../constants/enums";
import { RightPanel } from "../../../../components/RightPanel";
import { Inventory } from "../../../Inventory/Inventory";
import {
  getLowestOngoingStatus,
  hasAvailabilityInputs,
  isAvailabilityRelevantStatus,
} from "../../helpers/leadHelpers";
import { MuiNumberField } from "../../../../components/formsComponents/MuiNumberField";
import RelationAutoSearch from "../../../../components/AutoCompleteSearches/RelationAutoSearch";
import { formatIndianCurrency } from "../../../../components/Tabs/utils";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import { EventPostponedDialog } from "./EventPostponedDialog";
import produce from "immer";
import { EventDatePicker } from "../EventDatePicker/EventDatePicker";
import { IBooking } from "../../../../redux/booking/booking.types";
import { UpdateEventDialog } from "./UpdateEventDialog";
import { DuplicateCancelationReasonDialog } from "./DuplicateCancelationReasonDialog/DuplicateCancelationReasonDialog";

// Bottom-to-top slide transition for mobile dialog
const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});


interface IEventExtrasEditorProps {
  value: IEventExtras | string | null | undefined;
  onChange: (extras: IEventExtras) => void;
  disabled?: boolean;
}

// The API may hand this back as a JSON string rather than an already-parsed object
const getEventTitle = (value: IEventExtras | string | null | undefined): string => {
  let obj: IEventExtras = {};
  if (value && typeof value === "object" && !Array.isArray(value)) {
    obj = value;
  } else if (typeof value === "string" && value.trim() !== "") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        obj = parsed;
      }
    } catch { }
  }
  return obj.event_title || "";
};

const EventExtrasEditor: React.FC<IEventExtrasEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const eventTitle = getEventTitle(value);

  return (
    <CustomTextField
      label="Custom Title"
      fullWidth
      disabled={disabled}
      value={eventTitle}
      onChange={(e: any) => onChange({ event_title: e.target.value })}
    />
  );
};

interface IEventDetailsManagerProps {
  events: IMultipleEvent[];
  initialEvents?: IMultipleEvent[];
  onEventsChange: (events: IMultipleEvent[]) => void;
  errors: any;
  submitAttempted: boolean;
  companyOutletUuid: string;
  isEventDisabled: (index: number) => boolean;
  disableFormByStatus: boolean;
  onPaymentClick?: (leadUuid: string, leadData: ILead) => void;
  onCancellationClick?: (leadUuid: string) => void;
  onBookedOrReleaseClick?: (
    leadUuid: string,
    btnType: "BOOKED" | "RELEASE_HOLD",
  ) => void;
  loading?: boolean;
  hideAddActionButton?: boolean;
  onDeleteEventClick?: (
    leadUuid: string,
    onSuccess: (isSuccess: boolean) => void,
  ) => void;
  isLoadingMoreEvents?: boolean;
  // isVenueAvailable?: boolean | null;
  eventAvailabilityMap?: {
    [eventIndex: number]: {
      checked: boolean;
      availabilty: boolean | null;
      details: IBooking | null;
    };
  };

  leadData?: ILead;
  /** Optional advanceFilter passed to the drive-status API (Lead Status dropdown). */
  driveStatusAdvancedFilters?: IDatatableV2AdvancedSearchFilter;
  /** Event row expanded on first render (desktop), e.g. a prefilled event in a dialog. */
  defaultExpandedIndex?: number | null;
  /** Rendered inside a Dialog — lifts the calendar panel above it (drawers sit below modals). */
  isInsideDialog?: boolean;
}

export const EventDetailsManager: React.FC<IEventDetailsManagerProps> = ({
  events,
  initialEvents,
  onEventsChange,
  errors,
  submitAttempted,
  companyOutletUuid,
  isEventDisabled,
  disableFormByStatus,
  onPaymentClick,
  onCancellationClick,
  onBookedOrReleaseClick,
  loading = false,
  hideAddActionButton = false,
  onDeleteEventClick,
  isLoadingMoreEvents = false,
  // isVenueAvailable,
  leadData,
  eventAvailabilityMap,
  driveStatusAdvancedFilters,
  defaultExpandedIndex = null,
  isInsideDialog = false,
}) => {
  const theme = useTheme();
  const { ftDate } = useTimezoneFormatter();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { list: driveStatusList, loading: driveStatusLoading } = useSelector(
    (storeState: IStoreState) => storeState.leads.driveStatusList,
  );

  const [expandedEventIndex, setExpandedEventIndex] = useState<number | null>(
    defaultExpandedIndex,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [openInventory, setOpenInventory] = useState<any | null>(null);
  const [editingEventIndex, setEditingEventIndex] = useState<number | null>(
    null,
  );
  const [openPostponedDialog, setOpenPostponedDialog] = useState<{
    data: IMultipleEvent;
    index: number;
  } | null>(null);

  const [duplicateCancelatinDialog, setDuplicateCancelatinDialog] = useState<string | null>(null);

  const [defaultOngoingStatus, setDefaultOngoingStatus] =
    useState<IMasterStatus | null>(null);
  const [openUpdateEventDialog, setOpenUpdateEventDialog] = useState<{
    index: number;
    newEvent: IMultipleEvent;
  } | null>(null);

  // Set default ONGOING status from Redux store
  React.useEffect(() => {
    if (
      driveStatusLoading !== LoadState.Loaded ||
      driveStatusList.length === 0
    ) {
      return;
    }

    const ongoingStatus = getLowestOngoingStatus(driveStatusList);

    if (ongoingStatus) {
      // Map status_uuid to drive_status_uuid since API returns status_uuid
      const mappedStatus = {
        ...ongoingStatus,
        drive_status_uuid:
          ongoingStatus.drive_status_uuid || ongoingStatus.status_uuid,
      };
      setDefaultOngoingStatus(mappedStatus);
    }
  }, [driveStatusLoading, driveStatusList]);

  const createNewEvent = (): IMultipleEvent => ({
    leads_uuid: null,
    event_type: "",
    event_uuid: "",
    event_time: "",
    event_time_uuid: "",
    preferred_venue: null,
    preferred_venue_uuid: "",
    function_month: null,
    function_date: null,
    followup_date: null,
    status: defaultOngoingStatus?.name || "ONGOING",
    actual_status: defaultOngoingStatus?.actual_status || "ONGOING",
    drive_status_uuid: defaultOngoingStatus?.status_uuid || "",
    service: [],
    number_of_rooms: 0,
    number_of_guests: 0,
    payment_mode: null,
    cancellation_dead_reason: null,
    other_event_name: null,
    booking_date: null,
    customer_relative_name: null,
    relation_with_customer: null,
    estimated_rough_revenue: null,
    guest_budget: null,
    duplicate_reference_uuid: null,
    duplicate_reference_contact_number: null,
    is_archived: 0,
    extras: {},
  });

  const handleAddEvent = () => {
    const updatedList = [...(events || []), createNewEvent()];
    setExpandedEventIndex(updatedList.length - 1);
    onEventsChange(updatedList);

    // open edit dialog for the newly added event
    if (isMobile) {
      setEditingEventIndex(updatedList.length - 1);
      setEditDialogOpen(true);
    }
  };

  const handleDeleteEventLead = (index: number) => {
    const updatedList = events.filter((_, i) => i !== index);
    onEventsChange(updatedList);

    // if (eventLead.leads_uuid) {
    //   onDeleteEventClick?.(eventLead.leads_uuid || "", (isSuccess) => {
    //     if (isSuccess) {
    //       onEventsChange(events.filter((_, i) => i !== index));
    //     }
    //   });
    // } else {
    //   const updatedList = events.filter((_, i) => i !== index);
    //   onEventsChange(updatedList);
    // }
  };

  const handleUpdateEvent = (
    index: number,
    updates: Partial<IMultipleEvent>,
  ) => {
    const updatedList = events.map((item, i) =>
      i === index ? { ...item, ...updates } : item,
    );
    onEventsChange(updatedList);
    setOpenUpdateEventDialog(null);
  };

  const handlePostponeExistingEvent = (
    event: IMultipleEvent,
    index: number,
  ) => {
    setOpenPostponedDialog({
      data: {
        ...event,
        actual_status: "POSTPONED",
        status: "POSTPONED",
      },
      index,
    });
  };

  const handlePostponeSuccess = (index: number, updates: IMultipleEvent) => {
    onEventsChange(
      produce(events, (draftState) => {
        draftState[index] = updates;
      }),
    );
    setOpenPostponedDialog(null);
  };

  return (
    <>
      {/* Mobile Card View */}
      {isMobile ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            {events?.map((event: IMultipleEvent, index: number) => {
              const isAllDisabled = isEventDisabled(index);
              const isDeleted = Boolean(event.is_archived);
              const isPastDate = event.function_date
                ? moment(event.function_date).isBefore(moment(), "day")
                : false;
              const isDeleteDisabled = isAllDisabled && !isPastDate;
              return (
                <Paper
                  key={event.leads_uuid || index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    borderLeft: "3px solid",
                    borderLeftColor: "primary.main",
                    "&:hover": {
                      boxShadow: 2,
                      borderColor: "primary.main",
                      borderLeftColor: "primary.main",
                    },
                  }}
                  onClick={() => {
                    setEditingEventIndex(index);
                    setEditDialogOpen(true);
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="700"
                        color="primary.main"
                        noWrap
                        sx={{ mb: 0.5 }}
                      >
                        {event.event_type === "Other Social"
                          ? event.other_event_name || "Other Social"
                          : event.event_type || "--"}
                      </Typography>
                      <LeadStatusRenderer
                        status={event.status || "ONGOING"}
                        actualStatus={event.actual_status || "ONGOING"}
                      />
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Add Event">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddEvent();
                          }}
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1,
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.2,
                              ),
                            },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {!hideAddActionButton && (
                        <>
                          {isDeleted ? (
                            <Tooltip title="Restore Lead">
                              <IconButton
                                color="primary"
                                disabled={isAllDisabled}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // handleDeleteEventLead(index);
                                  handleUpdateEvent(index, {
                                    is_archived: 0,
                                  });
                                }}
                                size="small"
                              >
                                <Restore fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Delete Lead">
                              <IconButton
                                color="error"
                                disabled={isDeleteDisabled}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // handleDeleteEventLead(index);
                                  if (event.leads_uuid) {
                                    handleUpdateEvent(index, {
                                      is_archived: 1,
                                    });
                                  } else {
                                    handleDeleteEventLead(index);
                                  }
                                }}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Card Content */}
                  <Grid container spacing={1} rowSpacing={1}>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mb: 0.25,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Venue
                      </Typography>
                      <Typography variant="body2" fontWeight="600" noWrap>
                        {event.preferred_venue || "--"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mb: 0.25,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Event Date
                      </Typography>
                      <Typography variant="body2" fontWeight="600" noWrap>
                        {event.function_date
                          ? ftDate(event.function_date)
                          : "--"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mb: 0.25,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Guests
                      </Typography>
                      <Chip
                        label={event.number_of_guests || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 22, fontWeight: 600 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mb: 0.25,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Rooms
                      </Typography>
                      <Chip
                        label={event.number_of_rooms || 0}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 22, fontWeight: 600 }}
                      />
                    </Grid>
                  </Grid>


                  {/* {event.followup_date && (
                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: "1px dashed",
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            Follow-up Date
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="error.main"
                          >
                            {ftDate(event.followup_date)}
                          </Typography>
                        </Box>
                      )} */}
                </Paper>
              );
            })}

            {/* Mobile Skeleton while loading more events */}
            {isLoadingMoreEvents && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  background:
                    "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 2, mb: 1 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 2 }}
                />
              </Paper>
            )}
          </Box>
          {/* Empty State - Show "Add First Event" button when no events */}
          {(!events || events.length === 0) && !isLoadingMoreEvents && (
            <Button
              type="button"
              sx={{
                display: "flex",
                mx: "auto",
                my: 2,
                alignItems: "center",
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddEvent}
            >
              Add First Event
            </Button>
          )}
        </>
      ) : (
        /* Desktop Table View */
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            width: "100%",
            borderRadius: 2.4,
            overflow: "hidden",
          }}
        >
          <Table
            size="small"
            sx={{
              // Compact every cell: trim the wide default horizontal padding
              // and shrink status chips so the table packs into the available
              // width without needing to scroll.
              "& .MuiTableCell-root": {
                px: 0.75,
                py: 0.5,
              },
              "& .MuiChip-root": {
                height: 20,
                fontSize: "0.68rem",
              },
              "& .MuiChip-label": {
                px: 0.75,
              },
              // Last body row sits flush with the rounded bottom edge.
              "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root":
                {
                  borderBottom: "none",
                },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: (t) => t.palette.primary.main,
                  "& .MuiTableCell-root": {
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                    color: "#fff",
                    backgroundColor: "transparent",
                    borderBottom: "none",
                    py: 1,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Actions</TableCell>

                <TableCell>Event Type</TableCell>

                <TableCell>Lead Status</TableCell>

                <TableCell>Preferred Venue</TableCell>

                <TableCell>Event Date</TableCell>

                <TableCell>Event Time</TableCell>

                <TableCell align="right"># Guest</TableCell>

                <TableCell align="right"># Rooms</TableCell>

                <TableCell align="right">Estimated Rough Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(events || [])
                .map((event) => {
                  const showMonthPicker =
                    "show_month_picker" in event
                      ? event.show_month_picker
                      : !event.function_date && !!event.function_month;

                  return {
                    ...event,
                    show_month_picker: Boolean(showMonthPicker),
                  };
                })
                .map((event: IMultipleEvent, index: number) => {
                  const isAllDisabled = isEventDisabled(index);
                  const isDeleted = Boolean(event.is_archived);
                  const isPastDate = event.function_date
                    ? moment(event.function_date).isBefore(moment(), "day")
                    : false;
                  const isDeleteDisabled = isAllDisabled && !isPastDate;

                  const shouldDisableCustomerRelativeName =
                    event.relation_with_customer === "Self";
                  const availability = eventAvailabilityMap?.[index];
                  const eventAvailability = availability?.availabilty;

                  return (
                    <React.Fragment key={event.leads_uuid || index}>
                      {/* Summary Row - Always Visible */}
                      <TableRow
                        onClick={() => {
                          if (expandedEventIndex === index) {
                            setExpandedEventIndex(null);
                          } else {
                            setExpandedEventIndex(index);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            expandedEventIndex === index
                              ? (t) => alpha(t.palette.primary.main, 0.08)
                              : "inherit",
                          "&:hover": {
                            backgroundColor: (t) =>
                              alpha(t.palette.primary.main, 0.04),
                          },
                          "& .MuiTableCell-root": {
                            py: 1.2,
                            fontSize: "0.8125rem",
                            borderBottomColor: "divider",
                            whiteSpace: "nowrap",
                          },
                        }}
                      >
                        {/* Actions Column with Expand/Collapse */}
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              "& .MuiIconButton-root": { p: 0.25 },
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (expandedEventIndex === index) {
                                  setExpandedEventIndex(null);
                                } else {
                                  setExpandedEventIndex(index);
                                }
                              }}
                            >
                              {expandedEventIndex === index ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>

                            {!hideAddActionButton && (
                              <>
                                {isDeleted ? (
                                  <Tooltip title="Restore Lead">
                                    <IconButton
                                      color="primary"
                                      disabled={isAllDisabled}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // handleDeleteEventLead(index);
                                        handleUpdateEvent(index, {
                                          is_archived: 0,
                                        });
                                      }}
                                      size="small"
                                    >
                                      <Restore />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Delete Lead">
                                    <IconButton
                                      color="error"
                                      disabled={isDeleteDisabled}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // handleDeleteEventLead(index);
                                        if (event.leads_uuid) {
                                          handleUpdateEvent(index, {
                                            is_archived: 1,
                                          });
                                        } else {
                                          handleDeleteEventLead(index);
                                        }
                                      }}
                                      size="small"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}

                            {!hideAddActionButton && (
                              <Tooltip title="Add Event">
                                <IconButton
                                  color="primary"
                                  // disabled={isAllDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddEvent();
                                  }}
                                  size="small"
                                >
                                  <AddIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        {/* Event Type */}
                        <TableCell>
                          <CustomChip
                            color="primary"
                            content={
                              event.event_type === "Other Social"
                                ? event.other_event_name || "Other Social"
                                : event.event_type || "--"
                            }
                            size="small"
                          />
                        </TableCell>

                        {/* Lead Status */}
                        <TableCell>
                          <LeadStatusRenderer
                            status={event.status || "ONGOING"}
                            actualStatus={event.actual_status || "ONGOING"}
                          />
                        </TableCell>

                        {/* Preferred Venue */}
                        <TableCell>{event.preferred_venue || "--"}</TableCell>

                        {/* Event Date */}
                        <TableCell>
                          {event.function_date
                            ? ftDate(event.function_date)
                            : "--"}
                        </TableCell>

                        {/* Event Time */}
                        <TableCell>{event.event_time || "--"}</TableCell>

                        {/* <TableCell>
                            {event.followup_date
                              ? ftDate(event.followup_date)
                              : "--"}
                          </TableCell> */}

                        {/* # Guest */}
                        <TableCell align="right">
                          {event.number_of_guests || 0}
                        </TableCell>

                        {/* # Rooms */}
                        <TableCell align="right">
                          {event.number_of_rooms || 0}
                        </TableCell>
                        {/* Estimated Rough Revenue */}
                        <TableCell align="right">
                          {formatIndianCurrency(
                            event.estimated_rough_revenue || 0,
                          )}
                        </TableCell>

                        {/* guest budget */}
                        {/* <TableCell>
                          {formatIndianCurrency(event.guest_budget || 0)}
                        </TableCell> */}
                      </TableRow>

                      {/* Collapsible Row with Edit Form */}
                      <TableRow>
                        <TableCell
                          style={{
                            paddingBottom: 0,
                            paddingTop: 0,
                          }}
                          colSpan={9}
                        >
                          <Collapse
                            in={expandedEventIndex === index}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Grid container spacing={1.5} mt={0.5} mb={0.5}>
                              {/* Lead Status */}
                              <Grid item xs={12} lg={2.4}>
                                <MasterStatusAutoSearch
                                  label="Lead Status"
                                  name="status"
                                  company_outlet_uuid={companyOutletUuid}
                                  value={event.status}
                                  advancedFilters={driveStatusAdvancedFilters}
                                  disabled={isAllDisabled || isDeleted}
                                  error={
                                    submitAttempted &&
                                    !!(errors as any)[
                                    `multiple_events_list_${index}_status`
                                    ]
                                  }
                                  helperText={
                                    submitAttempted
                                      ? (errors as any)[
                                      `multiple_events_list_${index}_status`
                                      ]
                                      : ""
                                  }
                                  onSelect={(newValue) => {
                                    handleUpdateEvent(index, {
                                      status: newValue.name,
                                      actual_status: newValue.actual_status,
                                      drive_status_uuid:
                                        newValue.status_uuid || "",
                                      ...(newValue.actual_status !==
                                        "HOLD" && {
                                        show_month_picker: false,
                                      }),
                                    });
                                  }}
                                />
                              </Grid>

                              {/* Cancellation Reason - Show only for CANCELLED/DEAD status */}
                              {["CANCELLED", "DEAD"].includes(
                                event.actual_status || "",
                              ) && (
                                  <React.Fragment>
                                    <Grid item xs={12} lg={2.4}>
                                      <CancellationReasonAutoSearch
                                        required
                                        label="Cancellation Reason"
                                        value={
                                          event.cancellation_dead_reason ?? ""
                                        }
                                        disabled={isAllDisabled || isDeleted}
                                        placeholder="Search cancellation reasons..."
                                        // error={
                                        //   submitAttempted &&
                                        //   !!(errors as any)[
                                        //     `multiple_events_list_${index}_cancellation_dead_reason`
                                        //   ]
                                        // }
                                        // helperText={
                                        //   submitAttempted
                                        //     ? (errors as any)[
                                        //         `multiple_events_list_${index}_cancellation_dead_reason`
                                        //       ]
                                        //     : ""
                                        // }

                                        error={
                                          !!(errors as any)[
                                          `multiple_events_list_${index}_cancellation_dead_reason`
                                          ]
                                        }
                                        helperText={
                                          (errors as any)[
                                          `multiple_events_list_${index}_cancellation_dead_reason`
                                          ]
                                        }
                                        onSelect={(selectedReason: any) => {
                                          handleUpdateEvent(index, {
                                            cancellation_dead_reason:
                                              selectedReason?.name || "",
                                          });
                                          if (selectedReason?.master_cancellation_reason_name?.toUpperCase() === "DUPLICATE") {
                                            setDuplicateCancelatinDialog(String(index))
                                          }
                                        }}
                                      />
                                    </Grid>
                                    {event.cancellation_dead_reason?.toUpperCase() === "DUPLICATE" && (
                                      <Grid item xs={12} lg={2.4}>
                                        <CustomTextField
                                          label="Duplicate Contact Number"
                                          name="duplicate_reference_contact_number"
                                          value={event.duplicate_reference_contact_number || ""}
                                          disabled={true}
                                          fullWidth
                                        />
                                      </Grid>
                                    )}
                                  </React.Fragment>
                                )}

                              {/* Event Type */}
                              <Grid item xs={12} lg={2.4}>
                                <EventAutoSearch
                                  label="Event Type *"
                                  value={event.event_type}
                                  onSelect={(
                                    event_type: any,
                                    event_uuid: any,
                                  ) => {
                                    handleUpdateEvent(index, {
                                      event_type,
                                      event_uuid,
                                    });
                                  }}
                                  multiple={false}
                                  disabled={isAllDisabled || isDeleted}
                                  error={
                                    submitAttempted &&
                                    !!(errors as any)[
                                    `multiple_events_list_${index}_event_type`
                                    ]
                                  }
                                  helperText={
                                    submitAttempted
                                      ? (errors as any)[
                                      `multiple_events_list_${index}_event_type`
                                      ]
                                      : ""
                                  }
                                />
                              </Grid>

                              {/* Payment Mode - Show only for CORPORATE events */}
                              {event.event_type === "Corporate" && (
                                <Grid item xs={12} lg={2.4}>
                                  <ControlledCustomSelect
                                    label="Payment Mode"
                                    variant="outlined"
                                    size="small"
                                    type="string"
                                    name="payment_mode"
                                    fullWidth
                                    value={event.payment_mode || ""}
                                    disabled={isAllDisabled || isDeleted}
                                    onChange={(e) => {
                                      handleUpdateEvent(index, {
                                        payment_mode: e.target.value as
                                          | "BILL_TO_COMPANY"
                                          | "DIRECT",
                                      });
                                    }}
                                    displayEmpty
                                    renderValue={renderDropdownValue(
                                      `Select Payment Mode`,
                                    )}
                                    error={
                                      submitAttempted &&
                                      !!(errors as any)[
                                      `multiple_events_list_${index}_payment_mode`
                                      ]
                                    }
                                    helperText={
                                      submitAttempted
                                        ? (errors as any)[
                                        `multiple_events_list_${index}_payment_mode`
                                        ]
                                        : ""
                                    }
                                    options={[
                                      {
                                        label: "Bill To Company",
                                        value: "BILL_TO_COMPANY",
                                      },
                                      { label: "Direct", value: "DIRECT" },
                                    ]}
                                  />
                                </Grid>
                              )}

                              {/* Event Name - Only for Other Social */}
                              {event.event_type === "Other Social" && (
                                <Grid item xs={12} lg={2.4}>
                                  <CustomTextField
                                    label="Event Name"
                                    name="other_event_name"
                                    value={event.other_event_name || ""}
                                    fullWidth
                                    disabled={isAllDisabled || isDeleted}
                                    onChange={(e) => {
                                      handleUpdateEvent(index, {
                                        other_event_name: e.target.value,
                                      });
                                    }}
                                    error={
                                      submitAttempted &&
                                      !!(errors as any)[
                                      `multiple_events_list_${index}_other_event_name`
                                      ]
                                    }
                                    helperText={
                                      submitAttempted
                                        ? (errors as any)[
                                        `multiple_events_list_${index}_other_event_name`
                                        ]
                                        : ""
                                    }
                                  />
                                </Grid>
                              )}

                              {/* Event Date */}
                              <Grid item xs={12} lg={2.4}>
                                <EventDatePicker
                                  values={event}
                                  disabled={isAllDisabled || isDeleted}
                                  onChange={(newEvt) => {

                                    // setOpenUpdateEventDialog({
                                    //   index,
                                    //   newEvent: newEvt,
                                    // });

                                    handleUpdateEvent(index, {
                                      function_date: newEvt.function_date,
                                      function_month: newEvt.function_month,
                                      show_month_picker: newEvt.show_month_picker,
                                      event_time_uuid: newEvt.event_time_uuid,
                                      event_time: newEvt.event_time,
                                      preferred_venue_uuid:
                                        newEvt.preferred_venue_uuid,
                                      preferred_venue: newEvt.preferred_venue,
                                    });
                                  }}
                                  error={
                                    submitAttempted
                                      ? (errors as any)[
                                      `multiple_events_list_${index}_function_date`
                                      ]
                                      : ""
                                  }
                                />
                              </Grid>

                              {/* Event Time */}
                              <Grid item xs={12} lg={2.4}>
                                <EventTimeAutoSearch
                                  label="Event Time"
                                  fullWidth
                                  event_timing_uuid={event.event_time_uuid}
                                  event_name={event.event_time}
                                  disabled={isAllDisabled || isDeleted}
                                  value={{
                                    event_timing_uuid: event.event_time_uuid,
                                    event_name: event.event_time,
                                  }}
                                  error={
                                    submitAttempted
                                      ? (errors as any)[
                                      `multiple_events_list_${index}_event_time`
                                      ]
                                      : ""
                                  }
                                  onSelect={(newValue: any) => {
                                    handleUpdateEvent(index, {
                                      event_time_uuid:
                                        newValue.event_timing_uuid ?? "",
                                      event_time: newValue.event_name ?? "",
                                    });
                                  }}
                                />
                              </Grid>

                              {/* Preferred Venue */}
                              <Grid item xs={12} lg={2.4}>
                                <VenueAutoSearch
                                  label="Preferred Venue"
                                  disabled={isAllDisabled || isDeleted}
                                  venue_uuid={
                                    event.preferred_venue_uuid || undefined
                                  }
                                  venue_name={
                                    event.preferred_venue || undefined
                                  }
                                  value={{
                                    venue_uuid: event.preferred_venue_uuid,
                                    venue_name: event.preferred_venue,
                                  }}
                                  error={
                                    submitAttempted
                                      ? (errors as any)[
                                      `multiple_events_list_${index}_preferred_venue`
                                      ]
                                      : undefined
                                  }
                                  onSelect={(newValue: any) => {
                                    handleUpdateEvent(index, {
                                      preferred_venue_uuid:
                                        newValue.venue_uuid ?? "",
                                      preferred_venue:
                                        newValue.venue_name ?? "",
                                    });
                                  }}
                                />
                              </Grid>


                              {/* {["ONGOING", "POSTPONED"].includes(
                                  event.actual_status,
                                ) && (
                                    <Grid item xs={12} lg={2.4}>
                                      <CustomDatePicker
                                        placeholder="Followup Date *"
                                        fullWidth
                                        maxDate={
                                          event.function_date
                                            ? moment(event.function_date)
                                            : undefined
                                        }
                                        // minDate={moment()}
                                        value={event.followup_date || ""}
                                        disabled={isAllDisabled || isDeleted}
                                        onChange={(newValue) => {
                                          handleUpdateEvent(index, {
                                            followup_date: newValue,
                                          });
                                        }}
                                        errorMessage={
                                          submitAttempted
                                            ? (errors as any)[
                                            `multiple_events_list_${index}_followup_date`
                                            ]
                                            : ""
                                        }
                                      />
                                    </Grid>
                                  )} */}

                              {/* # Guest */}
                              <Grid item xs={12} lg={2.4}>
                                <CustomNumberField
                                  label="Number of Guests *"
                                  id={`number_of_guests_${index}`}
                                  disabled={isAllDisabled || isDeleted}
                                  value={event.number_of_guests || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 6) {
                                      handleUpdateEvent(index, {
                                        number_of_guests: Number(value),
                                      });
                                    }
                                  }}
                                  error={
                                    submitAttempted &&
                                    !!(errors as any)[
                                    `multiple_events_list_${index}_number_of_guests`
                                    ]
                                  }
                                  helperText={
                                    submitAttempted
                                      ? (errors as any)[
                                      `multiple_events_list_${index}_number_of_guests`
                                      ]
                                      : ""
                                  }
                                />
                              </Grid>

                              {/* # Rooms */}
                              <Grid item xs={12} lg={2.4}>
                                <CustomNumberField
                                  label="Number of Rooms"
                                  id={`number_of_rooms_${index}`}
                                  disabled={isAllDisabled || isDeleted}
                                  value={event.number_of_rooms || 0}
                                  onChange={(e) => {
                                    handleUpdateEvent(index, {
                                      number_of_rooms: Number(e.target.value),
                                    });
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} lg={2.4}>
                                <CustomCurrencyFieldV3
                                  fullWidth
                                  label="Estimated Rough Revenue"
                                  id={`estimated_rough_revenue_${index}`}
                                  disabled={isAllDisabled}
                                  value={
                                    event.estimated_rough_revenue?.toString() ||
                                    ""
                                  }
                                  onChange={(value) => {
                                    handleUpdateEvent(index, {
                                      estimated_rough_revenue:
                                        value === "" ? null : value,
                                    });
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} lg={2.4}>
                                <CustomCurrencyFieldV3
                                  fullWidth
                                  label="Guest Budget"
                                  id={`guest_budget_${index}`}
                                  disabled={isAllDisabled}
                                  value={event.guest_budget?.toString() || ""}
                                  onChange={(value) => {
                                    handleUpdateEvent(index, {
                                      guest_budget:
                                        value === "" ? null : value,
                                    });
                                  }}
                                />
                              </Grid>

                              {/* <Grid item xs={12} lg={2.4}>
                                <RelationAutoSearch
                                  label="Relation with customer"
                                  disabled={isAllDisabled}
                                  value={event.relation_with_customer || ""}
                                  onChange={(value) => {
                                    handleUpdateEvent(index, {
                                      relation_with_customer: value,
                                    });
                                  }}
                                  isSelftIncluded
                                />
                              </Grid>

                              <Grid item xs={12} lg={2.4}>
                                <CustomTextField
                                  fullWidth
                                  label={"Customer Relative Name"}
                                  name="customer_relative_name"
                                  InputLabelProps={{ shrink: true }}
                                  disabled={
                                    isAllDisabled ||
                                    shouldDisableCustomerRelativeName
                                  }
                                  value={
                                    shouldDisableCustomerRelativeName
                                      ? leadData?.name_of_guest
                                      : event.customer_relative_name
                                  }
                                  onChange={(e: any) => {
                                    handleUpdateEvent(index, {
                                      customer_relative_name: e.target.value,
                                    });
                                  }}
                                />
                              </Grid> */}

                              {/* Custom Title — key/value fields shown on the outlet quotation cover page */}
                              <Grid item xs={12} lg={2.4}>
                                <EventExtrasEditor
                                  value={event.extras}
                                  disabled={isAllDisabled || isDeleted}
                                  onChange={(rows) => {
                                    handleUpdateEvent(index, {
                                      extras: rows,
                                    });
                                  }}
                                />
                              </Grid>
                            </Grid>

                            {eventAvailability === true &&
                              hasAvailabilityInputs(event) &&
                              isAvailabilityRelevantStatus(
                                event.actual_status,
                              ) && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    border: 2,
                                    borderColor: "success.light",
                                    bgcolor: "rgba(46, 125, 50, 0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 40,
                                      height: 40,
                                      borderRadius: "50%",
                                      bgcolor: "rgba(46, 125, 50, 0.15)",
                                    }}
                                  >
                                    <CheckCircleOutline
                                      sx={{
                                        color: "success.main",
                                        fontSize: 24,
                                      }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={600}
                                      color={"success.main"}
                                    >
                                      Venue Available!
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <strong>{event.preferred_venue}</strong>{" "}
                                      on{" "}
                                      <strong>
                                        {ftDate(event.function_date || "")}
                                      </strong>
                                    </Typography>
                                  </Box>
                                </Paper>
                              )}

                            {eventAvailability === false &&
                              hasAvailabilityInputs(event) &&
                              isAvailabilityRelevantStatus(
                                event.actual_status,
                              ) && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    border: 2,
                                    borderColor: "error.light",
                                    bgcolor: "rgba(211, 47, 47, 0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 40,
                                      height: 40,
                                      borderRadius: "50%",
                                      bgcolor: "rgba(211, 47, 47, 0.15)",
                                    }}
                                  >
                                    <CancelOutlined
                                      sx={{
                                        color: "error.main",
                                        fontSize: 24,
                                      }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={600}
                                      color={"error.main"}
                                    >
                                      "Venue Not Available"
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <strong>{event.preferred_venue}</strong>{" "}
                                      on{" "}
                                      <strong>
                                        {ftDate(event.function_date || "")}
                                      </strong>
                                    </Typography>
                                  </Box>
                                </Paper>
                              )}

                            <Stack
                              direction="row"
                              spacing={1.5}
                              marginTop={1.5}
                              marginBottom={1}
                              justifyContent="flex-end"
                            >
                              {(() => {
                                const initialEvent = initialEvents?.[index];

                                const initialStatus =
                                  initialEvent?.actual_status;

                                return (
                                  <>
                                    <Button
                                      type="button"
                                      variant="contained"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setOpenInventory(event);
                                      }}
                                    >
                                      <CalendarMonth sx={{ mr: 1 }} /> Open
                                      Calender
                                    </Button>

                                    {/* Make Payment button - only show if initial status was BOOKED */}
                                    {initialStatus === "BOOKED" &&
                                      event.leads_uuid !== "" &&
                                      !hideAddActionButton &&
                                      onPaymentClick && (
                                        <Button
                                          type="button"
                                          variant="contained"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onPaymentClick(
                                              event.leads_uuid || "",
                                              event as ILead,
                                            );
                                          }}
                                        >
                                          <CurrencyRupee /> Make Payment
                                        </Button>
                                      )}

                                    {/* Cancellation button - only show if initial status was BOOKED */}
                                    {initialStatus === "BOOKED" &&
                                      event.actual_status !== "CANCELLED" &&
                                      event.actual_status !== "DEAD" &&
                                      onCancellationClick && (
                                        <>
                                          <Button
                                            type="button"
                                            disabled={loading}
                                            variant="contained"
                                            sx={{
                                              backgroundColor: "error.main",
                                              "&:hover": {
                                                backgroundColor: "error.dark",
                                              },
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onCancellationClick(
                                                event.leads_uuid || "",
                                              );
                                            }}
                                          >
                                            Cancellation
                                          </Button>

                                          <EventDatePicker
                                            type="button"
                                            values={event}
                                            onChange={(newEvt) => {
                                              handleUpdateEvent(index, {
                                                function_date:
                                                  newEvt.function_date,
                                                function_month:
                                                  newEvt.function_month,
                                                show_month_picker:
                                                  newEvt.show_month_picker,
                                                event_time_uuid:
                                                  newEvt.event_time_uuid,
                                                event_time: newEvt.event_time,
                                                preferred_venue_uuid:
                                                  newEvt.preferred_venue_uuid,
                                                preferred_venue:
                                                  newEvt.preferred_venue,
                                                actual_status: "POSTPONED",
                                                status: "POSTPONED",
                                              });
                                            }}
                                          />
                                          {/* <Button
                                            type="button"
                                            disabled={loading}
                                            variant="contained"
                                            sx={{
                                              backgroundColor: "success.main",
                                              "&:hover": {
                                                backgroundColor: "success.dark",
                                              },
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handlePostponeExistingEvent(
                                                event,
                                                index,
                                              );
                                            }}
                                          >
                                            Reschedule
                                          </Button> */}
                                        </>
                                      )}

                                    {/* Booked button - only show if initial status was HOLD */}
                                    {initialStatus === "HOLD" &&
                                      event.actual_status !== "CANCELLED" &&
                                      event.actual_status !== "DEAD" &&
                                      onBookedOrReleaseClick && (
                                        <>
                                          <Button
                                            type="button"
                                            disabled={loading}
                                            variant="contained"
                                            sx={{
                                              backgroundColor: "success.dark",
                                              "&:hover": {
                                                backgroundColor:
                                                  "success.main",
                                              },
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onBookedOrReleaseClick(
                                                event.leads_uuid || "",
                                                "BOOKED",
                                              );
                                            }}
                                          >
                                            Booked Now
                                          </Button>

                                          <Button
                                            type="button"
                                            disabled={loading}
                                            variant="contained"
                                            sx={{
                                              backgroundColor: "primary.main",
                                              "&:hover": {
                                                backgroundColor:
                                                  "primary.dark",
                                              },
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onBookedOrReleaseClick(
                                                event.leads_uuid || "",
                                                "RELEASE_HOLD",
                                              );
                                            }}
                                          >
                                            Release Hold
                                          </Button>
                                        </>
                                      )}
                                  </>
                                );
                              })()}
                            </Stack>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}

              {/* Skeleton rows while loading more events from API */}
              {isLoadingMoreEvents && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box sx={{ py: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        height={48}
                        sx={{ borderRadius: 1, mb: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        height={48}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {/* Empty State - Show "Add First Event" button when no events */}
              {(!events || events.length === 0) && !isLoadingMoreEvents && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 4 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddEvent}
                    >
                      Add First Event
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile Edit Dialog - Bottom to Top Animation */}
      <Dialog
        fullScreen
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingEventIndex(null);
        }}
        TransitionComponent={SlideTransition}
        sx={{
          "& .MuiDialog-paper": {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: "90vh",
            marginTop: "10vh",
          },
        }}
      >
        {editingEventIndex !== null && events?.[editingEventIndex] && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            {/* Dialog Header */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderBottom: "1px solid",
                borderColor: "divider",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="600" color="primary.main">
                  Edit Event Details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {events[editingEventIndex].event_type || "New Event"}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingEventIndex(null);
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Dialog Content */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2,
                backgroundColor: "#f8f9fa",
              }}
            >
              <Grid container spacing={2}>
                {/* Render all the form fields for the event */}
                {(() => {
                  const event = events[editingEventIndex];
                  const index = editingEventIndex;
                  const isAllDisabled =
                    isEventDisabled(index) || Boolean(event.is_archived);

                  const shouldDisableCustomerRelativeName =
                    event.relation_with_customer === "Self";

                  return (
                    <React.Fragment>
                      {/* Lead Status */}
                      <Grid item xs={12}>
                        <MasterStatusAutoSearch
                          label="Lead Status"
                          name="status"
                          company_outlet_uuid={companyOutletUuid}
                          value={event.status}
                          disabled={isAllDisabled}
                          error={
                            submitAttempted &&
                            !!(errors as any)[
                            `multiple_events_list_${index}_status`
                            ]
                          }
                          helperText={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_status`
                              ]
                              : ""
                          }
                          onSelect={(newValue: any) => {
                            handleUpdateEvent(index, {
                              status: newValue.name,
                              actual_status: newValue.actual_status,
                              drive_status_uuid: newValue.status_uuid || "",
                            });
                          }}
                        />
                      </Grid>

                      {/* Event Type */}
                      <Grid item xs={12}>
                        <EventAutoSearch
                          label="Event Type *"
                          value={event.event_type}
                          onSelect={(event_type: any, event_uuid: any) => {
                            handleUpdateEvent(index, {
                              event_type,
                              event_uuid,
                            });
                          }}
                          multiple={false}
                          disabled={isAllDisabled}
                          error={
                            submitAttempted &&
                            !!(errors as any)[
                            `multiple_events_list_${index}_event_type`
                            ]
                          }
                          helperText={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_event_type`
                              ]
                              : ""
                          }
                        />
                      </Grid>

                      {/* Event Name - Only for Other Social */}
                      {event.event_type === "Other Social" && (
                        <Grid item xs={12}>
                          <CustomTextField
                            label="Event Name"
                            name="other_event_name"
                            value={event.other_event_name || ""}
                            fullWidth
                            disabled={isAllDisabled}
                            onChange={(e) => {
                              handleUpdateEvent(index, {
                                other_event_name: e.target.value,
                              });
                            }}
                            error={
                              submitAttempted &&
                              !!(errors as any)[
                              `multiple_events_list_${index}_other_event_name`
                              ]
                            }
                            helperText={
                              submitAttempted
                                ? (errors as any)[
                                `multiple_events_list_${index}_other_event_name`
                                ]
                                : ""
                            }
                          />
                        </Grid>
                      )}

                      {/* Event Time */}
                      <Grid item xs={12}>
                        <EventTimeAutoSearch
                          label="Event Time"
                          event_timing_uuid={event.event_time_uuid}
                          event_name={event.event_time}
                          disabled={isAllDisabled}
                          fullWidth
                          value={{
                            event_timing_uuid: event.event_time_uuid,
                            event_name: event.event_time,
                          }}
                          error={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_event_time`
                              ]
                              : undefined
                          }
                          onSelect={(newValue: any) => {
                            handleUpdateEvent(index, {
                              event_time_uuid: newValue.event_timing_uuid ?? "",
                              event_time: newValue.event_name ?? "",
                            });
                          }}
                        />
                      </Grid>

                      {/* Preferred Venue */}
                      <Grid item xs={12}>
                        <VenueAutoSearch
                          label="Preferred Venue"
                          venue_uuid={event.preferred_venue_uuid || undefined}
                          venue_name={event.preferred_venue || undefined}
                          disabled={isAllDisabled}
                          value={{
                            venue_uuid: event.preferred_venue_uuid,
                            venue_name: event.preferred_venue,
                          }}
                          error={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_preferred_venue`
                              ]
                              : undefined
                          }
                          onSelect={(newValue: any) => {
                            handleUpdateEvent(index, {
                              preferred_venue_uuid: newValue.venue_uuid ?? "",
                              preferred_venue: newValue.venue_name ?? "",
                            });
                          }}
                        />
                      </Grid>

                      {/* Number of Guests */}
                      <Grid item xs={12} sm={6}>
                        <MuiNumberField
                          label="Number of Guests *"
                          name="number_of_guests"
                          value={event.number_of_guests || ""}
                          disabled={isAllDisabled}
                          onChange={(e) => {
                            handleUpdateEvent(index, {
                              number_of_guests: parseInt(e.target.value),
                            });
                          }}
                          error={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_number_of_guests`
                              ]
                              : ""
                          }
                        />
                      </Grid>

                      {/* Number of Rooms */}
                      <Grid item xs={12} sm={6}>
                        <MuiNumberField
                          label="Number of Rooms"
                          name="number_of_rooms"
                          value={event.number_of_rooms || ""}
                          disabled={isAllDisabled}
                          onChange={(e) => {
                            handleUpdateEvent(index, {
                              number_of_rooms: parseInt(e.target.value),
                            });
                          }}
                        />
                      </Grid>

                      {/* Event Date */}
                      <Grid item xs={12}>
                        <CustomDatePicker
                          placeholder="Event Date"
                          fullWidth
                          value={event.function_date || null}
                          disabled={isAllDisabled}
                          onChange={(newValue) => {
                            let extractedMonth = "";
                            if (newValue) {
                              const date = new Date(newValue);
                              extractedMonth = date.toLocaleString("default", {
                                month: "long",
                              });
                            }
                            handleUpdateEvent(index, {
                              function_date: newValue,
                              function_month: extractedMonth,
                            });
                          }}
                          errorMessage={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_function_date`
                              ]
                              : ""
                          }
                        />
                      </Grid>

                      {/* Event Month */}
                      <Grid item xs={12}>
                        <ControlledCustomSelect
                          label="Event Month"
                          error={
                            submitAttempted &&
                            !!(errors as any)[
                            `multiple_events_list_${index}_function_month`
                            ]
                          }
                          helperText={
                            submitAttempted
                              ? (errors as any)[
                              `multiple_events_list_${index}_function_month`
                              ]
                              : ""
                          }
                          variant="outlined"
                          size="small"
                          type="string"
                          name="function_month"
                          fullWidth
                          value={event.function_month || ""}
                          disabled={isAllDisabled}
                          onChange={(e: any) => {
                            handleUpdateEvent(index, {
                              function_month: e.target.value,
                              function_date: null,
                            });
                          }}
                          displayEmpty
                          renderValue={renderDropdownValue(`Select Month`)}
                          options={[
                            { label: "January", value: "January" },
                            { label: "February", value: "February" },
                            { label: "March", value: "March" },
                            { label: "April", value: "April" },
                            { label: "May", value: "May" },
                            { label: "June", value: "June" },
                            { label: "July", value: "July" },
                            { label: "August", value: "August" },
                            { label: "September", value: "September" },
                            { label: "October", value: "October" },
                            { label: "November", value: "November" },
                            { label: "December", value: "December" },
                          ]}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={12} lg={2.4}>
                        <CustomCurrencyFieldV3
                          fullWidth
                          label="Estimated Rough Revenue"
                          id={`estimated_rough_revenue_${index}`}
                          disabled={isAllDisabled}
                          value={
                            event.estimated_rough_revenue?.toString() || ""
                          }
                          onChange={(value) => {
                            handleUpdateEvent(index, {
                              estimated_rough_revenue:
                                value === "" ? null : value,
                            });
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={12} lg={2.4}>
                        <CustomCurrencyFieldV3
                          fullWidth
                          label="Guest Budget"
                          id={`guest_budget_${index}`}
                          disabled={isAllDisabled}
                          value={event.guest_budget?.toString() || ""}
                          onChange={(value) => {
                            handleUpdateEvent(index, {
                              guest_budget: value === "" ? null : value,
                            });
                          }}
                        />
                      </Grid>

                      {/* <Grid item xs={12} lg={2.4}>
                        <RelationAutoSearch
                          label="Relation with customer"
                          disabled={isAllDisabled}
                          value={event.relation_with_customer || ""}
                          onChange={(value) => {
                            handleUpdateEvent(index, {
                              relation_with_customer: value,
                            });
                          }}
                          isSelftIncluded
                        />
                      </Grid>

                      <Grid item xs={12} lg={2.4}>
                        <CustomTextField
                          fullWidth
                          disabled={
                            isAllDisabled || shouldDisableCustomerRelativeName
                          }
                          label={"Customer Relative Name"}
                          name="customer_relative_name"
                          value={
                            shouldDisableCustomerRelativeName
                              ? leadData?.name_of_guest
                              : event.customer_relative_name
                          }
                          onChange={(e: any) => {
                            handleUpdateEvent(index, {
                              customer_relative_name: e.target.value,
                            });
                          }}
                        />
                      </Grid> */}


                      {/* {["ONGOING", "POSTPONED"].includes(
                        event.actual_status,
                      ) && (
                          <Grid item xs={12}>
                            <CustomDatePicker
                              placeholder="Followup Date *"
                              fullWidth
                              // maxDate={
                              //   event.function_date
                              //     ? moment(event.function_date)
                              //     : undefined
                              // }
                              minDate={moment()}
                              value={event.followup_date || ""}
                              disabled={isAllDisabled}
                              onChange={(newValue) => {
                                handleUpdateEvent(index, {
                                  followup_date: newValue,
                                });
                              }}
                              errorMessage={
                                submitAttempted
                                  ? (errors as any)[
                                  `multiple_events_list_${index}_followup_date`
                                  ]
                                  : ""
                              }
                            />
                          </Grid>
                        )} */}

                      {/* Payment Mode - Show only for CORPORATE events */}
                      {event.event_type === "Corporate" && (
                        <Grid item xs={12}>
                          <ControlledCustomSelect
                            label="Payment Mode"
                            variant="outlined"
                            size="small"
                            type="string"
                            name="payment_mode"
                            fullWidth
                            value={event.payment_mode || ""}
                            disabled={isAllDisabled}
                            onChange={(e) => {
                              handleUpdateEvent(index, {
                                payment_mode: e.target.value as
                                  | "BILL_TO_COMPANY"
                                  | "DIRECT",
                              });
                            }}
                            displayEmpty
                            renderValue={renderDropdownValue(
                              `Select Payment Mode`,
                            )}
                            error={
                              submitAttempted &&
                              !!(errors as any)[
                              `multiple_events_list_${index}_payment_mode`
                              ]
                            }
                            helperText={
                              submitAttempted
                                ? (errors as any)[
                                `multiple_events_list_${index}_payment_mode`
                                ]
                                : ""
                            }
                            options={[
                              {
                                label: "Bill To Company",
                                value: "BILL_TO_COMPANY",
                              },
                              { label: "Direct", value: "DIRECT" },
                            ]}
                          />
                        </Grid>
                      )}

                      {/* Cancellation Reason - Show only for CANCELLED/DEAD status */}
                      {["CANCELLED", "DEAD"].includes(
                        event.actual_status || "",
                      ) && (
                          <Grid item xs={12}>
                            <CancellationReasonAutoSearch
                              required
                              label="Cancellation Reason"
                              value={event.cancellation_dead_reason ?? ""}
                              disabled={isAllDisabled}
                              placeholder="Search cancellation reasons..."
                              error={
                                submitAttempted &&
                                !!(errors as any)[
                                `multiple_events_list_${index}_cancellation_dead_reason`
                                ]
                              }
                              helperText={
                                submitAttempted
                                  ? (errors as any)[
                                  `multiple_events_list_${index}_cancellation_dead_reason`
                                  ]
                                  : ""
                              }
                              onSelect={(selectedReason: any) => {
                                handleUpdateEvent(index, {
                                  cancellation_dead_reason:
                                    selectedReason?.name || "",
                                });
                              }}
                            />
                          </Grid>
                        )}

                      {/* Custom Title — key/value fields shown on the outlet quotation cover page */}
                      <Grid item xs={12}>
                        <EventExtrasEditor
                          value={event.extras}
                          disabled={isAllDisabled}
                          onChange={(rows) => {
                            handleUpdateEvent(index, {
                              extras: rows,
                            });
                          }}
                        />
                      </Grid>
                    </React.Fragment>
                  );
                })()}
              </Grid>
            </Box>

            {/* Dialog Footer with Save Button */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderTop: "1px solid",
                borderColor: "divider",
                boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
                display: "flex",
                gap: 2,
              }}
            >
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingEventIndex(null);
                }}
              >
                Close
              </Button>

              <Button
                type="button"
                fullWidth
                variant="contained"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingEventIndex(null);
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {openInventory && (
        <RightPanel
          open={true}
          onClose={() => setOpenInventory(null)}
          width="96%"
          heading="Inventory Report"
          drawerProps={
            isInsideDialog
              ? { zIndex: (t: any) => t.zIndex.modal + 1 }
              : undefined
          }
        >
          <Inventory eventLeadData={openInventory} isFromEventLead={true} />
        </RightPanel>
      )}

      {openPostponedDialog && (
        <EventPostponedDialog
          open={true}
          data={openPostponedDialog.data}
          onSaveSuccess={(data) =>
            handlePostponeSuccess(openPostponedDialog.index, data)
          }
          onClose={() => setOpenPostponedDialog(null)}
        />
      )}
      {duplicateCancelatinDialog !== null && (
        <DuplicateCancelationReasonDialog
          open={true}
          onSelect={(data) => {
            const eventIndex = Number(duplicateCancelatinDialog);
            console.log("duplicateCancelatinDialog data ==> ", data);
            setDuplicateCancelatinDialog(null);
            if (!Number.isNaN(eventIndex)) {
              handleUpdateEvent(eventIndex, {
                duplicate_reference_uuid: data.leads_uuid,
                duplicate_reference_contact_number: data.contact_number_one,
              });
            }
          }}
          onClose={() => setDuplicateCancelatinDialog(null)}
        />
      )}

      {openUpdateEventDialog?.newEvent && (
        <UpdateEventDialog
          updatedEventValues={openUpdateEventDialog.newEvent}
          onClose={() => setOpenUpdateEventDialog(null)}
          onConfirmClick={() =>
            handleUpdateEvent(openUpdateEventDialog.index, {
              function_date: openUpdateEventDialog.newEvent.function_date,
              function_month: openUpdateEventDialog.newEvent.function_month,
              show_month_picker:
                openUpdateEventDialog.newEvent.show_month_picker,
              event_time_uuid: openUpdateEventDialog.newEvent.event_time_uuid,
              event_time: openUpdateEventDialog.newEvent.event_time,
              preferred_venue_uuid:
                openUpdateEventDialog.newEvent.preferred_venue_uuid,
              preferred_venue: openUpdateEventDialog.newEvent.preferred_venue,
            })
          }
        />
      )}
    </>
  );
};
